import { ReviewCommentRecord } from '@mesa/core/review';
import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Editor } from '@tiptap/react';
import { formatDistanceToNow } from 'date-fns';
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FilterIcon,
  MessageSquareDashedIcon,
  MessageSquareIcon,
  UserIcon,
  XIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  RefreshCwIcon,
} from 'lucide-react';
import { createContext, memo, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ulid } from 'ulid';
import { VList, VListHandle, CustomItemComponentProps } from 'virtua';

import { AIChatView } from './ai-chat';
import { CommentResolutionButton } from './comment-resolution-button';
import { CommentResolutionHoverButton } from './comment-resolution-hover-button';
import { ResolvedCommentIndicator } from './resolved-comment-indicator';

import { CommentEditor } from '@/components/editor/comment-editor';
import { MarkdownEditor } from '@/components/editor/markdown-editor';
import { ReactionBar } from '@/components/reactions';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { OutdatedCommentIndicator } from '@/features/pulls/components/outdated-comment-indicator';
import { useSidebar } from '@/features/pulls/context/sidebar-context';
import { getTimeAgo } from '@/lib/time';
import { cn } from '@/lib/utils';
import { useZero } from '@/lib/zero';
import { collaboratorsQuery } from '@/services/repo';
import { useUser } from '@/services/user';

// Custom hook for review comments data
function useReviewComments() {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const { user } = useUser();
  const z = useZero();

  // Fetch review comments and pull request data using Zero
  const [reviewComments] = useZeroQuery(
    z.query.ReviewCommentsTable.where(({ and, cmp }) =>
      and(
        cmp('owner', owner), 
        cmp('repo', repo), 
        cmp('pullNumber', Number(number)),
        cmp('isBad', false)
      )
    )
  );

  const [pull] = useZeroQuery(
    z.query.PullsTable.where(({ and, cmp }) =>
      and(cmp('owner', owner), cmp('repo', repo), cmp('number', Number(number)))
    ).one()
  );

  // Get all parent review comments (not replies)
  const parentReviewComments = useMemo(() => {
    if (!reviewComments) return [];
    return reviewComments
      .filter((c) => c.inReplyToId === null)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [reviewComments]);

  // Get replies for a specific comment
  const getReplies = useCallback(
    (id: number): ReviewCommentRecord[] => {
      if (!reviewComments) return [];
      return reviewComments.filter((c) => c.inReplyToId === id);
    },
    [reviewComments]
  );

  // Create review comment mutation
  const createReviewComment = async ({
    body,
    line,
    path,
    startLine,
    inReplyToId,
    side,
  }: {
    body: string;
    line: number;
    path: string;
    startLine: number | null;
    inReplyToId: number;
    side: 'LEFT' | 'RIGHT';
  }) => {
    if (!pull) return;

    z.mutate.ReviewCommentsTable.insert({
      gid: ulid(),
      author: {
        login: user.login,
        id: user.id,
        avatarUrl: user.avatar_url,
      },
      authorLogin: user.login,
      commitId: pull.head.sha,
      path,
      side,
      startLine,
      line,
      inReplyToId,
      owner,
      repo,
      pullNumber: Number(number),
      body,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  };

  return {
    parentReviewComments,
    getReplies,
    createReviewComment,
  };
}

// Component-specific loading skeleton that matches the sidebar layout
function CommentsSidebarSkeleton() {
  return (
    <div className="flex flex-col w-[420px] h-full pb-2 overflow-hidden contain-layout">
      {/* Header */}
      <div className="p-3 border-b">
        <Skeleton className="h-5 w-20" />
      </div>

      {/* Filter controls skeleton */}
      <div className="p-3 space-y-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-8 w-full" />
      </div>

      {/* Comments list skeleton */}
      <div className="flex-1 overflow-hidden p-3 space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="border rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-3 w-12" />
            </div>
            <Skeleton className="h-12 w-full" />
            <div className="flex gap-2">
              <Skeleton className="h-6 w-8" />
              <Skeleton className="h-6 w-8" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Component-specific error fallback
function CommentsSidebarError({ resetErrorBoundary }: { resetErrorBoundary: () => void }) {
  return (
    <div className="flex flex-col w-[420px] h-full pb-2 overflow-hidden contain-layout items-center justify-center p-6">
      <AlertTriangleIcon className="h-12 w-12 text-destructive mb-4" />
      <h3 className="text-lg font-semibold mb-2">Unable to load comments</h3>
      <p className="text-muted-foreground mb-4 text-center text-sm">
        There was an error loading the review comments. Please try again.
      </p>
      <Button onClick={resetErrorBoundary} variant="outline" size="sm">
        <RefreshCwIcon className="h-4 w-4 mr-2" />
        Try Again
      </Button>
    </div>
  );
}

export function CommentsSidebar() {
  return (
    <ErrorBoundary FallbackComponent={CommentsSidebarError}>
      <Suspense fallback={<CommentsSidebarSkeleton />}>
        <CommentsSidebarInner />
      </Suspense>
    </ErrorBoundary>
  );
}

type CommentListContextType = {
  getReplies: (id: number) => ReviewCommentRecord[];
  activeComment: ReviewCommentRecord | undefined;
  setActiveComment: (comment: ReviewCommentRecord | undefined) => void;
  jumpedToCommentGid: string | undefined;
  parentReviewComments: ReviewCommentRecord[];
  createReviewComment: (comment: {
    body: string;
    line: number;
    path: string;
    startLine: number | null;
    inReplyToId: number;
    side: 'LEFT' | 'RIGHT';
  }) => void;
  filteredComments: ReviewCommentRecord[];
};

const CommentListContext = createContext<CommentListContextType>({
  getReplies: () => [],
  activeComment: undefined,
  setActiveComment: () => {},
  jumpedToCommentGid: undefined,
  parentReviewComments: [],
  createReviewComment: () => {},
  filteredComments: [],
});

const useCommentListContext = () => {
  return useContext(CommentListContext);
};

const CommentsSidebarInner = memo(() => {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });

  const { parentReviewComments, createReviewComment, getReplies } = useReviewComments();

  // Use the sidebar context instead of local state
  const { activeCommentGid, dispatch, activeFilePath, activeLineNumber, activeTab, areResolvedCommentsCollapsed } =
    useSidebar();

  // AI Chat now uses useChat hook - no manual state management needed

  // Derive the active comment from the activeCommentGid
  const activeComment = useMemo(() => {
    if (!activeCommentGid) return undefined;
    return parentReviewComments?.find((comment) => comment.gid === activeCommentGid);
  }, [activeCommentGid, parentReviewComments]);

  const [jumpedToCommentGid, setJumpedToCommentGid] = useState<string | undefined>(undefined);

  // Apply filters from context
  const filteredComments = useMemo(() => {
    let filtered = parentReviewComments;

    // Apply file and line filters
    if (activeFilePath) {
      filtered = filtered.filter((comment) => {
        if (comment.path !== activeFilePath) return false;
        if (activeLineNumber !== null) {
          return comment.line === activeLineNumber || comment.startLine === activeLineNumber;
        }
        return true;
      });
    }

    // Apply resolved comments collapsing
    if (areResolvedCommentsCollapsed) {
      filtered = filtered.filter((comment) => !comment.resolvedById);
    }

    return filtered;
  }, [parentReviewComments, activeFilePath, activeLineNumber, areResolvedCommentsCollapsed]);

  // Set up navigation functions that replace the old window.jumpToComment etc.
  useEffect(() => {
    // Add flash effect when navigating to a comment
    if (activeCommentGid) {
      setJumpedToCommentGid(activeCommentGid);

      // Reset flash after delay
      setTimeout(() => {
        setJumpedToCommentGid(undefined);
        // Only remove temporary highlights, preserving permanent ones
        removeTemporaryHighlights();
      }, 1000);
    }
  }, [activeCommentGid]);

  // Default view - show all parent comments with reply count buttons
  return (
    <div className="flex flex-col w-[420px] h-full overflow-hidden contain-layout min-w-0">
      <CommentListContext.Provider
        value={{
          getReplies,
          activeComment,
          setActiveComment: (comment) => {
            if (comment) {
              dispatch({ type: 'SET_ACTIVE_COMMENT_GID', payload: comment.gid });
            } else {
              dispatch({ type: 'SET_ACTIVE_COMMENT_GID', payload: null });
            }
          },
          parentReviewComments,
          jumpedToCommentGid,
          createReviewComment,
          filteredComments,
        }}
      >
        {activeComment ? (
          <ThreadView />
        ) : (
          <div className="flex flex-col h-full gap-0">
            {activeTab === 'comments' && (
              <div className="flex-1 overflow-hidden flex flex-col">
                <AllCommentsView />
              </div>
            )}
            {activeTab === 'ai-chat' && (
              <div className="flex-1 overflow-hidden flex flex-col">
                <AIChatView />
              </div>
            )}
          </div>
        )}
      </CommentListContext.Provider>
    </div>
  );
});

function ThreadView() {
  const { owner, repo } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });

  const { data: collaborators } = useQuery(collaboratorsQuery(owner, repo));

  const { activeComment, setActiveComment, jumpedToCommentGid, getReplies, createReviewComment } =
    useCommentListContext();

  const replyCount = useCallback(
    (id: number) => {
      return getReplies(id).length;
    },
    [getReplies]
  );

  const activeCommentReplies = useMemo(
    () => (activeComment ? getReplies(activeComment.id!) : []),
    [activeComment, getReplies]
  );

  const editorRef = useRef<Editor>(null);
  const [comment, setComment] = useState('');
  const previousCommentGidRef = useRef<string | undefined>(undefined);

  // Apply permanent highlight to the active comment's code block
  useEffect(() => {
    if (activeComment) {
      // We only scroll if we're navigating to a different comment
      // Use GID to detect if this comment is a reply. If the GID is the same don't scroll
      if (activeComment.gid && previousCommentGidRef.current === activeComment.gid) {
        return;
      }

      // Update the ref to track this comment for future comparisons
      if (activeComment.gid) {
        previousCommentGidRef.current = activeComment.gid;
      }

      const path = activeComment.path;
      const startLine = activeComment.startLine ?? activeComment.line;
      const endLine = activeComment.line;
      const side = activeComment.side;

      // Only proceed if we have valid line numbers
      if (!startLine || !endLine) return;

      // Find the file container
      const fileContainer = document.querySelector(`[data-file-path="${path}"]`);
      if (!fileContainer) return;

      // Find the diff table
      const diffTable = fileContainer.querySelector('.mesa-diff');
      if (!diffTable) return;

      // Attribute to look for based on side
      const sideAttr = side === 'LEFT' ? 'data-old-line-number' : 'data-new-line-number';

      // Find all elements to highlight
      const linesToHighlight: Element[] = [];
      for (let currentLine = startLine; currentLine <= endLine; currentLine++) {
        const lineElement = findLineElement(diffTable, currentLine, side, sideAttr);
        if (lineElement) {
          linesToHighlight.push(lineElement);
        }
      }

      // Apply highlight without duration (permanent until removed)
      if (linesToHighlight.length > 0) {
        // Ensure the diff is expanded
        const header = fileContainer.querySelector('.sticky');
        if (header?.classList.contains('border-b') === false) {
          // Expand the diff if collapsed
          const expandButton = header?.querySelector('button');
          expandButton?.click();
        }

        // Apply highlights without duration (permanent until removed)
        const highlightClass = side === 'LEFT' ? 'mesa-highlight-left' : 'mesa-highlight-right';
        linesToHighlight.forEach((el) => {
          const rowElement = el.tagName === 'TR' ? el : el.closest('tr');
          if (rowElement) {
            rowElement.classList.add(highlightClass);
          }
        });

        // Scroll to the first line
        if (linesToHighlight[0]) {
          const row = linesToHighlight[0].tagName === 'TR' ? linesToHighlight[0] : linesToHighlight[0].closest('tr');
          if (row) {
            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    } else {
      // No active comment, reset the ref
      previousCommentGidRef.current = undefined;
    }

    // Clean up function removes highlights when component unmounts or activeComment changes
    return () => {
      removeExistingHighlights();
    };
  }, [activeComment]);

  if (!activeComment) return null; // This should never happen

  return (
    <div className="flex flex-col h-full overflow-auto w-[420px] pb-2 min-w-0">
      <div className="w-full flex items-center justify-between px-3 sticky top-0 z-10 shadow-sm py-2">
        <p className="font-medium">Thread</p>
        <div
          className="cursor-pointer hover:bg-accent rounded-sm px-2 py-1 flex items-center justify-center overflow-hidden gap-2"
          onMouseDown={() => setActiveComment(undefined)}
        >
          <ArrowLeftIcon className="size-4" />
          <span className="text-sm">Back to all</span>
        </div>
      </div>

      <div className="group flex items-center gap-2 text-xs bg-muted text-foreground border border-border rounded-md m-2 p-2">
        <span title={activeComment.path}>
          {activeComment.path}
          &nbsp; &nbsp;
          {activeComment.startLine && (
            <>
              <LineNumber side={activeComment.side} lineNumber={activeComment.startLine} />
              &ndash;
            </>
          )}
          {activeComment.line && <LineNumber side={activeComment.side} lineNumber={activeComment.line} />}
        </span>
        <OutdatedCommentIndicator comment={activeComment} />
      </div>
      <Comment
        comment={activeComment}
        isActive={true}
        replyCount={0} // We're showing replies below in the list
        setActiveComment={setActiveComment}
        jumpedToCommentGid={jumpedToCommentGid}
        owner={owner}
      />

      <div className="flex w-full items-center">
        <p className="text-xs font-light text-nowrap text-gray-500 py-3 px-3">{activeCommentReplies.length} Replies</p>
        <Separator orientation="horizontal" className="flex-1" />
      </div>

      {activeCommentReplies.length > 0 && (
        <div className="mt-2">
          {activeCommentReplies.map((reply) => (
            <Comment
              key={reply.id}
              comment={reply}
              replyCount={0} // Don't show reply counts for replies in thread view
              setActiveComment={() => {}}
              jumpedToCommentGid={undefined}
              owner={owner}
            />
          ))}
        </div>
      )}

      <div className="px-2 pb-2">
        <CommentEditor
          owner={owner}
          ref={editorRef}
          value={comment}
          onChange={setComment}
          onSubmit={() => {
            if (activeComment.id && activeComment.line) {
              createReviewComment({
                body: comment,
                path: activeComment.path,
                inReplyToId: activeComment.id,
                side: activeComment.side,
                line: activeComment.line,
                startLine: activeComment.startLine,
              });
              editorRef.current?.commands.clearContent();
              setComment('');
            }
          }}
          placeholder="Write a reply..."
          className="w-full"
          isExpanded={true}
          editable={true}
          mentionCollaborators={collaborators}
        />
      </div>
    </div>
  );
}

function AllCommentsView() {
  const { owner } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const { parentReviewComments, filteredComments } = useCommentListContext();

  // Count resolved comments for summary
  const resolvedCommentsCount = useMemo(() => {
    return parentReviewComments.filter((comment) => comment.resolvedById !== null).length;
  }, [parentReviewComments]);

  // Get sidebar context for filters
  const { activeFilePath, activeLineNumber, areResolvedCommentsCollapsed, dispatch } = useSidebar();

  // Replace local state with context
  const [isFileOpen, setIsFileOpen] = useState(false);

  const scrollRef = useRef<VListHandle>(null);

  // Get unique file paths from comments
  const uniqueFilePaths = useMemo(() => {
    const paths = new Set<string>();
    parentReviewComments.forEach((comment) => {
      if (comment.path) {
        paths.add(comment.path);
      }
    });
    return Array.from(paths).sort();
  }, [parentReviewComments]);

  // Get unique line numbers from the selected file
  const availableLines = useMemo(() => {
    if (!activeFilePath) return [];

    const lines = new Set<number>();
    parentReviewComments.forEach((comment) => {
      if (comment.path === activeFilePath) {
        if (comment.line) lines.add(comment.line);
        if (comment.startLine) lines.add(comment.startLine);
      }
    });

    return Array.from(lines).sort((a, b) => a - b);
  }, [parentReviewComments, activeFilePath]);

  // Build a flattened item list with date headers and dividers
  const items = useMemo(() => {
    const flattened: Array<{ type: 'header' | 'comment' | 'divider'; data: any }> = [];
    let lastDateLabel: string | null = null;

    filteredComments.forEach((comment) => {
      const dateLabel = formatDate(comment.createdAt);

      // Insert a divider before every new date group except the very first one.
      if (lastDateLabel !== dateLabel) {
        if (flattened.length > 0) {
          flattened.push({ type: 'divider', data: null });
        }
        flattened.push({ type: 'header', data: dateLabel });
        lastDateLabel = dateLabel;
      }

      flattened.push({ type: 'comment', data: comment });
    });

    return flattened;
  }, [filteredComments]);

  // Calculate indices of sticky headers (only the "header" items – the badge itself)
  const stickyIndexes = useMemo(() => {
    return items.reduce<number[]>((acc, item, idx) => {
      if (item.type === 'header') acc.push(idx);
      return acc;
    }, []);
  }, [items]);

  // Track which header should currently stick to the top
  const [activeIndex, setActiveIndex] = useState<number>(0);

  // Reset scroll position when filters change
  useEffect(() => {
    if (items.length > 0) {
      scrollRef.current?.scrollToIndex(items.length - 1);
    }
  }, [items]);

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Filter UI */}
      <div className="flex-none border-b px-3 pb-1 shadow-xs">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Resolved Comments Toggle */}
            {resolvedCommentsCount > 0 && (
              <div className="flex items-center gap-2">
                <Switch
                  id="hide-resolved"
                  checked={areResolvedCommentsCollapsed}
                  onCheckedChange={(collapsed) =>
                    dispatch({ type: 'SET_RESOLVED_COMMENTS_COLLAPSED', payload: collapsed })
                  }
                />
                <Label htmlFor="hide-resolved" className="text-xs text-muted-foreground cursor-pointer">
                  Hide resolved ({resolvedCommentsCount})
                </Label>
              </div>
            )}

            {/* File Filter Button Group */}
            <div className="flex items-center border border-stone-300 rounded-md overflow-hidden">
              <Popover open={isFileOpen} onOpenChange={setIsFileOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'text-xs px-3 h-8 rounded-none border-0 gap-1 min-w-0',
                      activeFilePath ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' : 'hover:bg-stone-100'
                    )}
                    data-filter-button
                  >
                    <FilterIcon className="size-3 shrink-0" />
                    <span className="truncate max-w-32">
                      {activeFilePath ? activeFilePath.split('/').pop() || activeFilePath : 'Filter by file'}
                    </span>
                    <ChevronDownIcon className="size-3 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-72" align="end">
                  <Command>
                    <CommandInput placeholder="Search files..." />
                    <CommandList className="max-h-72">
                      <CommandEmpty>No files found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          className={!activeFilePath ? 'font-bold' : ''}
                          onSelect={() => {
                            dispatch({ type: 'SET_ACTIVE_FILE_PATH', payload: null });
                            dispatch({ type: 'SET_ACTIVE_LINE_NUMBER', payload: null });
                            setIsFileOpen(false);
                          }}
                        >
                          All files
                        </CommandItem>
                        <CommandGroup heading="Files">
                          {uniqueFilePaths.map((path) => (
                            <CommandItem
                              key={path}
                              className={activeFilePath === path ? 'font-bold' : ''}
                              onSelect={() => {
                                dispatch({ type: 'SET_ACTIVE_FILE_PATH', payload: path });
                                dispatch({ type: 'SET_ACTIVE_LINE_NUMBER', payload: null });
                                setIsFileOpen(false);
                              }}
                              data-file-path={path}
                            >
                              <div className="truncate">{path}</div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Line Filter - numeric input */}
              {activeFilePath && (
                <>
                  <div className="w-px bg-stone-300"></div>
                  <Input
                    type="number"
                    min="1"
                    placeholder="Line"
                    value={activeLineNumber || ''}
                    onChange={(e) => {
                      const val = e.target.value ? parseInt(e.target.value) : null;
                      dispatch({ type: 'SET_ACTIVE_LINE_NUMBER', payload: isNaN(val!) ? null : val });
                    }}
                    className="w-20 h-8 text-xs border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                    data-line-input
                  />
                  <div className="w-px bg-stone-300"></div>
                  {/* Clear single filter button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 border-0 rounded-none px-2 hover:bg-stone-100"
                    onClick={() =>
                      activeLineNumber
                        ? dispatch({ type: 'SET_ACTIVE_LINE_NUMBER', payload: null })
                        : dispatch({ type: 'SET_ACTIVE_FILE_PATH', payload: null })
                    }
                  >
                    <XIcon className="size-3" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {filteredComments.length === 0 && (
        <div className="flex-1 flex items-center justify-center">
          {activeFilePath ? (
            <FilterEmptyState
              fileFilter={activeFilePath}
              onClearFilter={() => {
                dispatch({ type: 'SET_ACTIVE_FILE_PATH', payload: null });
                dispatch({ type: 'SET_ACTIVE_LINE_NUMBER', payload: null });
              }}
            />
          ) : areResolvedCommentsCollapsed && resolvedCommentsCount > 0 ? (
            <ResolvedEmptyState
              resolvedCount={resolvedCommentsCount}
              onShowResolved={() => dispatch({ type: 'SET_RESOLVED_COMMENTS_COLLAPSED', payload: false })}
            />
          ) : (
            <EmptyState />
          )}
        </div>
      )}

      {items.length > 0 && (
        <div className="flex-1 overflow-hidden">
          <StickyIndexContext.Provider value={{ stickyIndexes, activeIndex }}>
            <VList
              ref={scrollRef}
              reverse
              className="h-full py-2 pr-2 overflow-x-hidden min-w-0"
              item={StickyItem as any}
              keepMounted={activeIndex >= 0 && activeIndex < items.length ? [activeIndex] : []}
              onScroll={() => {
                if (!scrollRef.current) return;
                const start = scrollRef.current.findStartIndex();
                const activeStickyIndex = [...stickyIndexes].reverse().find((idx) => start >= idx);
                if (activeStickyIndex !== undefined) {
                  setActiveIndex(activeStickyIndex);
                }
              }}
            >
              {items.map((item, idx) => {
                switch (item.type) {
                  case 'divider':
                    return (
                      <div key={`divider-${idx}`} className="relative py-2">
                        <Separator className="w-full" />
                      </div>
                    );
                  case 'header':
                    return (
                      <div
                        key={`header-${idx}`}
                        className="flex justify-center items-center -mt-4.5 h-4 overflow-visible pointer-events-auto"
                      >
                        <Badge
                          variant="outline"
                          className="z-10 text-xs rounded-full px-4 py-1.5 bg-stone-50 shadow-2xs"
                        >
                          {item.data}
                        </Badge>
                      </div>
                    );
                  default:
                    return (
                      <CommentItem
                        key={`comment-${idx}`}
                        comment={item.data}
                        owner={owner}
                        onSetLineFilter={(line) => {
                          if (activeFilePath) {
                            dispatch({ type: 'SET_ACTIVE_LINE_NUMBER', payload: line });
                          }
                        }}
                      />
                    );
                }
              })}
            </VList>
          </StickyIndexContext.Provider>
        </div>
      )}
    </div>
  );
}

const FilterEmptyState = memo(({ fileFilter, onClearFilter }: { fileFilter: string; onClearFilter: () => void }) => {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="rounded-full bg-stone-200 p-4 mb-4">
        <FilterIcon className="size-8 text-stone-600" />
      </div>
      <h3 className="text-lg font-medium mb-2">No comments found</h3>
      <p className="text-sm text-stone-500 max-w-md mb-4">
        There are no comments for <span className="font-medium">{fileFilter}</span>
      </p>
      <Button variant="outline" size="sm" onClick={onClearFilter}>
        Clear filter
      </Button>
    </div>
  );
});

const EmptyState = memo(() => {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="rounded-full bg-stone-200 p-4 mb-4">
        <MessageSquareDashedIcon className="size-8 text-stone-600" />
      </div>
      <h3 className="text-lg font-medium mb-2">No review comments yet</h3>
      <p className="text-sm text-stone-500 max-w-md mb-4">
        Review comments will appear here when someone adds comments to specific lines of code during review.
      </p>
    </div>
  );
});

const ResolvedEmptyState = memo(
  ({ resolvedCount, onShowResolved }: { resolvedCount: number; onShowResolved: () => void }) => {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <div className="rounded-full bg-green-100 p-4 mb-4">
          <CheckCircleIcon className="size-8 text-green-600" />
        </div>
        <h3 className="text-lg font-medium mb-2">All comments resolved</h3>
        <p className="text-sm text-stone-500 max-w-md mb-4">
          {resolvedCount === 1
            ? 'There is 1 resolved conversation hidden.'
            : `There are ${resolvedCount} resolved conversations hidden.`}
        </p>
        <Button variant="outline" size="sm" onClick={onShowResolved}>
          Show resolved comments
        </Button>
      </div>
    );
  }
);

export const Comment = memo(
  ({
    comment,
    replyCount,
    isActive,
    setActiveComment,
    jumpedToCommentGid,
    owner,
  }: {
    comment: ReviewCommentRecord;
    replyCount: number;
    isActive?: boolean;
    setActiveComment: (comment: ReviewCommentRecord) => void;
    jumpedToCommentGid?: string;
    owner: string;
  }) => {
    const showFlash = jumpedToCommentGid === comment.gid;
    return (
      <div
        key={comment.gid}
        className={cn(
          'group flex w-full p-3 min-w-0',
          showFlash && 'animate-flash',
          (comment.resolvedById || comment.isOutdated) && 'opacity-60'
        )}
      >
        <div className={cn('relative flex flex-col w-full min-w-0')}>
          <div className="flex items-center gap-2 mb-2">
            <Avatar className="size-5">
              {comment.author && <AvatarImage src={comment.author.avatarUrl} alt={comment.author.login} />}
              <AvatarFallback>
                <UserIcon className="size-4" />
              </AvatarFallback>
            </Avatar>
            <span className="text-sm font-semibold leading-0">{comment.authorLogin.replace(/\[bot\]/, '')}</span>
            {comment.authorLogin.endsWith('[bot]') && (
              <div className="text-xs px-2 text-muted-foreground border border-stone-300 rounded-full">Bot</div>
            )}
            <span className="text-xs text-gray-500 font-light">{getTimeAgo(comment.createdAt)}</span>
          </div>

          <MarkdownEditor
            owner={owner}
            value={comment.body ?? ''}
            editable={false}
            className="text-sm select-text [&_.ProseMirror]:!p-0 [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:break-words"
          />

          {/* Reactions */}
          {comment.id && <ReactionBar subjectId={comment.id} commentType="review" className="mt-2" />}

          {/* Resolution UI for review comments - full UI in thread view, indicator only in list view */}
          <div className="mt-2">
            {isActive ? (
              <div className="flex items-center justify-between">
                <ResolvedCommentIndicator comment={comment} />
                <CommentResolutionButton comment={comment} />
              </div>
            ) : (
              comment.resolvedById && <ResolvedCommentIndicator comment={comment} />
            )}
          </div>

          {replyCount > 0 && !isActive && (
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                className="w-full flex-1 hover:bg-gray-100 h-6 rounded-sm hover:cursor-pointer justify-start px-2"
                onClick={() => setActiveComment(comment)}
              >
                <p className="text-xs text-gray-500 ml-0">{replyCount} replies</p>
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }
);

function formatTime(date: string) {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date(date));
}

const CommentItem = ({
  comment,
  onSetLineFilter,
  owner,
}: {
  comment: ReviewCommentRecord;
  onSetLineFilter?: (line: number) => void;
  owner: string;
}) => {
  const { getReplies, setActiveComment, jumpedToCommentGid } = useCommentListContext();
  const replies = useMemo(() => {
    if (!comment.id) return [];
    return getReplies(comment.id);
  }, [getReplies, comment]);

  const lastestReply = useMemo(() => {
    if (!replies.length) return undefined;
    return replies.toSorted((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  }, [replies]);

  const uniqueAuthors = useMemo(() => {
    const seen = new Set<string>();
    const authors: { login: string; avatarUrl: string }[] = [];
    for (const reply of replies) {
      if (seen.has(reply.authorLogin)) continue;
      authors.push({ login: reply.authorLogin, avatarUrl: reply.author.avatarUrl });
      seen.add(reply.authorLogin);
    }
    return authors;
  }, [replies]);

  const numReplies = replies.length;

  return (
    <div className="flex flex-col pr-1 min-w-0">
      {/* Comment */}
      <div
        className={cn(
          'flex flex-col gap-2 pb-2 pt-4 pl-3 relative group',
          comment.isOutdated || comment.resolvedById ? 'opacity-60' : 'hover:bg-stone-100 cursor-pointer'
        )}
        onClick={() => {
          if (!comment.isOutdated && !comment.resolvedById) {
            jumpToBlock(comment);
          }
        }}
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 overflow-hidden">
            <img
              src={comment.author.avatarUrl}
              alt={comment.authorLogin}
              className="size-8 rounded-full border border-stone-600"
            />
          </div>
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-sm font-semibold leading-0">{comment.authorLogin.replace(/\[bot\]/, '')}</span>
              {comment.authorLogin.endsWith('[bot]') && (
                <div className="text-xs px-2 text-muted-foreground border border-stone-300 rounded-full">Bot</div>
              )}
              <span className="text-xs text-muted-foreground">{formatTime(comment.updatedAt)}</span>
            </div>
            <CommentContent
              key={comment.gid}
              comment={comment}
              jumpedToCommentGid={jumpedToCommentGid}
              onSetLineFilter={onSetLineFilter}
              owner={owner}
            />
          </div>
        </div>
        <ThreadReplyButton comment={comment} />
        <CommentResolutionHoverButton comment={comment} />
      </div>
      {/* Go to thread */}
      {numReplies > 0 && (
        <div className="pl-13 flex items-center">
          <div
            className={cn(
              'w-full px-1 -ml-1 flex items-center justify-between group h-8 rounded-sm cursor-pointer',
              'border border-transparent hover:border-stone-200 hover:bg-stone-50 hover:shadow-xs'
            )}
            onMouseDown={() => setActiveComment(comment)}
          >
            <div className="flex items-center flex-1 gap-2">
              <div className="flex items-center">
                {uniqueAuthors.slice(0, 2).map((author) => (
                  <img key={author.login} src={author.avatarUrl} alt={author.login} className="size-6 rounded-full" />
                ))}
                {uniqueAuthors.length === 3 && (
                  <img src={uniqueAuthors[2].avatarUrl} alt={uniqueAuthors[2].login} className="size-6 rounded-full" />
                )}
                {uniqueAuthors.length > 3 && (
                  <span className="size-6 rounded-full flex items-center justify-center bg-gray-100 text-xs">
                    +{uniqueAuthors.length - 2}
                  </span>
                )}
              </div>
              <p className="text-xs font-semibold text-sky-800">{numReplies} replies</p>
              <div className="relative flex-1 flex items-center">
                {lastestReply && (
                  <span className="text-xs text-muted-foreground absolute transition-all duration-250 opacity-100 group-hover:opacity-0 ease-in-out">
                    {formatDistanceToNow(lastestReply.createdAt, { addSuffix: true })}
                  </span>
                )}
                <span className="text-xs text-muted-foreground transition-all duration-250 opacity-0 ease-in-out group-hover:opacity-100">
                  View thread
                </span>
              </div>
            </div>
            <ChevronRightIcon className="size-4 text-gray-500 invisible group-hover:visible" />
          </div>
        </div>
      )}
    </div>
  );
};

const CommentContent = ({
  comment,
  jumpedToCommentGid,
  onSetLineFilter,
  owner,
}: {
  comment: ReviewCommentRecord;
  jumpedToCommentGid: string | undefined;
  onSetLineFilter?: (line: number) => void;
  owner: string;
}) => {
  const [showFullPath, setShowFullPath] = useState(false);
  const displayPath = useMemo(() => {
    if (showFullPath) return comment.path;
    const parts = comment.path.split('/');
    const filename = parts.pop() ?? '';
    return parts.length ? `.../${filename}` : filename;
  }, [showFullPath, comment]);

  const showFlash = jumpedToCommentGid === comment.gid;

  return (
    <div className={cn('flex flex-col gap-2 min-w-0', showFlash && 'animate-flash')}>
      <div className="group flex items-center gap-2 text-xs italic text-foreground border-l-2 border-yellow-500 pl-2">
        <span
          className={cn(comment.isOutdated ? '' : 'cursor-pointer hover:underline')}
          onClick={(e) => {
            e.stopPropagation();
            if (!comment.isOutdated) {
              // Toggle between truncated/full path display
              setShowFullPath((prev) => !prev);
            }
          }}
          title={comment.path}
        >
          {displayPath}
          &nbsp; &nbsp;
          {comment.startLine && (
            <>
              <LineNumber
                side={comment.side}
                lineNumber={comment.startLine}
                onClick={
                  onSetLineFilter && comment.startLine && !comment.isOutdated
                    ? () => onSetLineFilter(comment.startLine!)
                    : undefined
                }
              />
              &ndash;
            </>
          )}
          {comment.line && (
            <LineNumber
              side={comment.side}
              lineNumber={comment.line}
              onClick={
                onSetLineFilter && comment.line && !comment.isOutdated
                  ? () => onSetLineFilter(comment.line!)
                  : undefined
              }
            />
          )}
        </span>
        <OutdatedCommentIndicator comment={comment} />
      </div>

      <MarkdownEditor
        owner={owner}
        className="whitespace-pre-wrap text-sm select-text [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:break-words"
        value={comment.body}
        editable={false}
      />

      {/* Reactions */}
      {comment.id && <ReactionBar subjectId={comment.id} commentType="review" className="mt-2" />}

      {/* Resolution indicator for resolved comments */}
      {comment.resolvedById && (
        <div className="mt-2">
          <ResolvedCommentIndicator comment={comment} />
        </div>
      )}
    </div>
  );
};

function LineNumber({
  side,
  lineNumber,
  onClick,
}: {
  side: 'LEFT' | 'RIGHT';
  lineNumber: number;
  onClick?: () => void;
}) {
  return (
    <span
      className={cn(
        'text-xs',
        side === 'LEFT' ? 'text-red-700' : 'text-green-700',
        onClick && 'cursor-pointer hover:underline'
      )}
      onClick={(e) => {
        if (onClick) {
          e.stopPropagation();
          onClick();
        }
      }}
      title={onClick ? 'Filter by this line' : undefined}
    >
      {lineNumber}
    </span>
  );
}

function formatDate(date: string) {
  const inputDate = new Date(date);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  // Check for today/yesterday
  if (inputDate.toDateString() === now.toDateString()) {
    return 'Today';
  }
  if (inputDate.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).formatToParts(inputDate);

  // Find the day number
  const day = parts.find((p) => p.type === 'day')?.value;
  if (!day) return '';

  // Format with ordinal suffix using Intl.PluralRules
  const ordinal = new Intl.PluralRules('en-US', { type: 'ordinal' }).select(parseInt(day));
  const suffix = {
    one: 'st',
    two: 'nd',
    few: 'rd',
    many: 'th', // Required by TypeScript but won't occur in English
    zero: 'th', // Required by TypeScript but won't occur in English
    other: 'th',
  }[ordinal];

  // Replace the day with our ordinal version
  return parts.map((p) => (p.type === 'day' ? day + suffix : p.value)).join('');
}

const ThreadReplyButton = memo(({ comment }: { comment: ReviewCommentRecord }) => {
  const { setActiveComment } = useCommentListContext();

  if (comment.isOutdated) {
    return null;
  }

  return (
    <div
      className="absolute -top-2 right-2 z-10 w-7  h-6 rounded-sm flex items-center justify-center bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-250 cursor-pointer"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        setActiveComment(comment);
      }}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <MessageSquareIcon className="size-5 opacity-80" strokeWidth={1.5} />
          </TooltipTrigger>
          <TooltipContent>Reply in thread</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
});

function jumpToBlock(comment: ReviewCommentRecord) {
  if (comment.isOutdated) {
    return;
  }

  const startLine = comment.startLine ?? comment.line;
  const endLine = comment.line;
  const side = comment.side;
  const path = comment.path;

  if (typeof startLine !== 'number' || typeof endLine !== 'number') return;

  // Find the file container by its data attribute
  const fileContainer = document.querySelector(`[data-file-path="${path}"]`);
  if (!fileContainer) return;

  // Ensure file diff is expanded
  const header = fileContainer.querySelector('.sticky');
  if (header?.classList.contains('border-b') === false) {
    // Expand the diff if collapsed
    const expandButton = header?.querySelector('button');
    expandButton?.click();
    // Wait for expansion animation
    setTimeout(() => findAndHighlightBlock(fileContainer, startLine, endLine, side), 300);
    return;
  }

  // Already expanded, find and highlight immediately
  findAndHighlightBlock(fileContainer, startLine, endLine, side);
}

/**
 * Finds a range of lines in a diff and highlights them
 */
function findAndHighlightBlock(container: Element, startLine: number, endLine: number, side: 'LEFT' | 'RIGHT'): void {
  // Find the diff table
  const diffTable = container.querySelector('.mesa-diff');
  if (!diffTable) return;

  // Attribute to look for based on side
  const sideAttr = side === 'LEFT' ? 'data-old-line-number' : 'data-new-line-number';

  // Find the start line element to scroll to
  const startLineElement = findLineElement(diffTable, startLine, side, sideAttr);
  if (!startLineElement) return;

  // Highlight all lines in the range
  const linesToHighlight: Element[] = [];

  // If startLine and endLine are the same, just highlight one line
  if (startLine === endLine) {
    linesToHighlight.push(startLineElement);
  } else {
    // Otherwise, find all lines in the range
    for (let currentLine = startLine; currentLine <= endLine; currentLine++) {
      const lineElement = findLineElement(diffTable, currentLine, side, sideAttr);
      if (lineElement) {
        linesToHighlight.push(lineElement);
      }
    }
  }

  // Apply highlight and scroll to the start line
  highlightAndScroll(startLineElement, side, linesToHighlight);
}

/**
 * Find the target line element using multiple strategies
 */
function findLineElement(
  diffTable: Element,
  targetLine: number,
  side: 'LEFT' | 'RIGHT',
  sideAttr: string
): Element | null {
  // Strategy 1: Direct selector
  const lineSelector = `.diff-line[${sideAttr}="${targetLine}"]`;
  const element = diffTable.querySelector(lineSelector);
  if (element) return element;

  // Strategy 2: Find TR with matching attribute
  const allRows = Array.from(diffTable.querySelectorAll('tr.diff-line'));
  const matchingRow = allRows.find(
    (row) =>
      (side === 'LEFT' && row.getAttribute('data-old-line-number') == targetLine.toString()) ||
      (side === 'RIGHT' && row.getAttribute('data-new-line-number') == targetLine.toString())
  );
  if (matchingRow) return matchingRow;

  // Strategy 3: Find via cell with data attribute
  const cellSelector = `td[${sideAttr}="${targetLine}"]`;
  const cell = diffTable.querySelector(cellSelector);
  if (cell) {
    const row = cell.closest('tr');
    if (row) return row;
  }

  return null;
}

/**
 * Apply highlighting to multiple elements and scroll to the first element
 */
function highlightAndScroll(element: Element, side: 'LEFT' | 'RIGHT', elementsToHighlight: Element[] = []): void {
  // Ensure we have the row element to scroll to
  const row = element.tagName === 'TR' ? element : element.closest('tr');
  if (!row) return;

  // Scroll into view
  row.scrollIntoView({ behavior: 'smooth', block: 'center' });

  // Remove any existing highlights
  removeExistingHighlights();

  // Highlight the elements in the block
  highlightElements(row, side, elementsToHighlight, 2000);
}

/**
 * Show a highlight on lines that are in the range of a comment
 */
function highlightElements(
  element: Element,
  side: 'LEFT' | 'RIGHT',
  elementsToHighlight: Element[],
  duration?: number
): void {
  // Determine highlight class based on side
  const highlightClass = side === 'LEFT' ? 'mesa-transient-highlight-left' : 'mesa-transient-highlight-right';

  // If no elements to highlight or just one, use the scrolled element
  const targets = elementsToHighlight.length <= 1 ? [element] : elementsToHighlight;

  // Apply highlights to all target elements
  targets.forEach((el) => {
    const rowElement = el.tagName === 'TR' ? el : el.closest('tr');
    if (rowElement) {
      rowElement.classList.add(highlightClass);
    }
  });

  // Remove highlights after duration if specified
  if (duration) {
    setTimeout(() => {
      targets.forEach((el) => {
        const rowElement = el.tagName === 'TR' ? el : el.closest('tr');
        if (rowElement) {
          rowElement.classList.remove(highlightClass);
        }
      });
    }, duration);
  }
}

/**
 * Remove any existing highlights in the document
 */
function removeExistingHighlights(): void {
  document
    .querySelectorAll('.mesa-transient-highlight-left')
    .forEach((el) => el.classList.remove('mesa-transient-highlight-left'));
  document
    .querySelectorAll('.mesa-transient-highlight-right')
    .forEach((el) => el.classList.remove('mesa-transient-highlight-right'));
  document.querySelectorAll('.mesa-highlight-left').forEach((el) => el.classList.remove('mesa-highlight-left'));
  document.querySelectorAll('.mesa-highlight-right').forEach((el) => el.classList.remove('mesa-highlight-right'));
}

/**
 * Remove only the temporary highlights in the document
 */
function removeTemporaryHighlights(): void {
  document
    .querySelectorAll('.mesa-transient-highlight-left')
    .forEach((el) => el.classList.remove('mesa-transient-highlight-left'));
  document
    .querySelectorAll('.mesa-transient-highlight-right')
    .forEach((el) => el.classList.remove('mesa-transient-highlight-right'));
}

// ----------------------------------------------------------------------------------
// Sticky header helpers used by AllCommentsView (date badge sticking)
// ----------------------------------------------------------------------------------

const StickyIndexContext = createContext({
  stickyIndexes: [] as number[],
  activeIndex: -1,
});

const StickyItem = memo(({ children, style, index, ref }: CustomItemComponentProps) => {
  const { stickyIndexes, activeIndex } = useContext(StickyIndexContext);
  return (
    <div
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ref={ref}
      style={{
        ...style,
        ...(stickyIndexes.includes(index) && { zIndex: 1 }),
        ...(activeIndex === index && { position: 'sticky', top: 0 }),
      }}
    >
      {children}
    </div>
  );
});
// ----------------------------------------------------------------------------------
